'use client'

import { Spacer } from '@vinted/web-ui'
import { useState } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'
import {
  getConversationFeedbackRatingOptions,
  updateFeedbackRatingOptions,
  transformFeedbackRatingsResponse,
  UpdateFeedbackRatingOptionsArgs,
  agentSupportFeedbackClosed,
  agentSupportFeedbackV1Viewed,
  endConversationClicked,
  confirmComplaintResolved,
  FeedbackRatingModel,
} from '@marketplace-web/conversation-feedback/conversation-feedback-data'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import useHelpCenterSessionTracking from '@marketplace-web/help-center/help-center-session-tracking-util'
import { getFeedbackType } from '@marketplace-web/help-center-conversation-support/help-center-automated-feedback-feature'

import FeedbackFormModal from '../FeedbackFormModal'

const CesFeedbackModal = ({
  primary,
  title,
  inverse,
  theme,
  size,
  action,
}: Partial<ConversationActionProps>) => {
  const [ratings, setRatings] = useState<FeedbackRatingModel | undefined>(undefined)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const conversationId = useConversationContext().conversation?.id
  const { helpCenterSessionId } = useHelpCenterSessionTracking()
  const userId = useSession().user?.id
  const { track } = useTracking()

  const { fetch, isLoading: isFetchRatingsOptionsLoading } = useFetch(
    getConversationFeedbackRatingOptions,
  )

  async function handleModalClose() {
    setIsModalOpen(false)

    if (!conversationId || !helpCenterSessionId) return
    track(agentSupportFeedbackClosed({ helpCenterSessionId, threadId: conversationId.toString() }))
    await confirmComplaintResolved(conversationId.toString())
    reloadPage()
  }

  async function handleModalOpen() {
    if (!conversationId || !userId || !helpCenterSessionId) return

    track(
      endConversationClicked({
        helpCenterSessionId,
        threadId: conversationId.toString(),
        supportType: getFeedbackType(action),
      }),
    )

    const ratingsResponse = await fetch(userId, conversationId.toString())

    if (ratingsResponse.data?.feedback_ratings) {
      setRatings(transformFeedbackRatingsResponse(ratingsResponse.data))
      setIsModalOpen(true)
      track(
        agentSupportFeedbackV1Viewed({ helpCenterSessionId, threadId: conversationId.toString() }),
      )
    }
  }

  async function updateFeedbackOptions(
    data: Omit<UpdateFeedbackRatingOptionsArgs, 'conversationId' | 'userId'>,
  ) {
    if (!userId || !conversationId) return
    await updateFeedbackRatingOptions(Number(userId), conversationId.toString(), data)
    if (data.finalized) {
      setIsModalOpen(false)
      reloadPage()
    }
  }

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        onClick={handleModalOpen}
        isLoading={isFetchRatingsOptionsLoading}
      />
      <FeedbackFormModal
        isOpen={isModalOpen}
        ratings={ratings}
        onClose={handleModalClose}
        onSubmit={updateFeedbackOptions}
      />
      <Spacer />
    </>
  )
}

export default CesFeedbackModal
